import { unref } from 'vue';
import IntersectionObserver from '@core/util/intersection-observer';
import { Cookie } from '@vue-util/cookie';
import constants from './constants';
import prosEvents from './prosEvents';
import prosZones from './prosZones';

const giftZones = {
  BROWSE_ZONE_B: '/shop/valentines-day-gift-guide/for-her?id=112648',
  BROWSE_ZONE_D: '/shop/valentines-day-gift-guide/for-him?id=112660',
  BROWSE_ZONE_E: '/shop/valentines-day-gift-guide/jewelry-watches?id=112668',
  BROWSE_ZONE_F: '/shop/valentines-day-gift-guide/fragrance?id=112669',
  BROWSE_ZONE_G: '/shop/valentines-day-gift-guide/home?id=275456',
  BROWSE_ZONE_H: '/shop/valentines-day-gift-guide/beauty?id=112673',
  BROWSE_ZONE_I: '/shop/valentines-day-gift-guide/handbags-accessories?id=112654',
  BROWSE_ZONE_J: '/shop/valentines-day-gift-guide/for-kids?id=323376',
  BROWSE_ZONE_K: '/shop/valentines-day-gift-guide/cologne-grooming?id=112664',
  BROWSE_ZONE_L: '/shop/valentines-day-gift-guide/tech-gifts?id=157643',
};

export const failureEvent = ({ requester, zone } = {}) => `${requester}-${zone}${prosEvents.failure}`;

/**
 * input => output
 * undefined => undefined
 * PDP_ZONE_A => PDP_ZONE_A
 * PDP_ZONE_A|PDP_ZONE_B => PDP_ZONE_A
 * @param zones
 * @returns {*}
 */

export const fallbackZone = (zones) => (zones ? zones.split('|')[0] : zones);

const userNm = () => Cookie.get('UserName', 'MISCGCs');

export const getHeader = (headerText) => {
  let header = decodeURIComponent(headerText.replace(/\+/g, ' '));
  const headerVariables = header.match(/<<(.*?)>>/g);
  const customerNameLabel = 'CustomerName';
  const username = userNm();
  if (headerVariables) {
    headerVariables.forEach((dynamicStructure) => {
      let dynamicVariable = dynamicStructure.split('::')[0].replace(/<</, '');
      const defaultVariableValue = dynamicStructure
        .split('::')[1]
        .replace(/>>/, '');

      if (dynamicVariable.indexOf(customerNameLabel) !== -1) {
        if (username) {
          dynamicVariable = dynamicVariable.replace(/CustomerName/, username);
        } else {
          dynamicVariable = defaultVariableValue;
        }
      } else {
        dynamicVariable = defaultVariableValue;
      }
      header = header.replace(dynamicStructure, dynamicVariable);
    });
  }

  header = header.replace(/v_username/g, username);
  return header;
};

const allZonesRecommendationEmpty = (zonesJson, config) => {
  if (!zonesJson) {
    return true;
  }
  const zones = config.zone && config.zone.split('|');
  return (
    (zones || []).find(
      (zone) => zonesJson
        && Object.keys(zonesJson[`${config.requester}-${zone}`] || []).length > 0,
    ) === undefined
  );
};

export const needFccRecommendation = ({ config, properties, response }) => {
  if (!properties.fccFallbackEnabled) {
    return false;
  }
  return (
    !response
    || (allZonesRecommendationEmpty(response.recommendationsOnAllZones, config)
      && !(response?.recommendedItems?.length > 0))
  );
};
// eslint-disable-next-line max-len
export const needCategoryFallbackRecommendations = (response) => response?.uiKillSwitches?.enableCategoryFallback && !(response?.recommendedItems?.length > 0);

/**
 * input => output
 * undefined => undefined
 * PDP_ZONE_A => ""
 * PDP_ZONE_A|PDP_ZONE_B => "PDP_ZONE_B"
 *
 * @param zones
 * @returns {*}
 */
export const otherThanFallbackZones = (zones) => (zones ? zones.split('|').splice(1).join('|') : zones);

/**
 * Pass it http://someurl or https://someurl
 * it will return //someurl
 * @param url
 * @returns {*}
 */
export const protocolLessUrl = (url) => (url ? url.replace(/http[s]*:/, '') : url);

export const uuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  // eslint-disable-next-line no-bitwise
  const r = (Math.random() * 16) | 0;
  // eslint-disable-next-line no-bitwise,no-mixed-operators
  const v = c === 'x' ? r : (r & 0x3) | 0x8;
  return v.toString(16);
});

export const encodedChoiceId = ({
  controlGroupId,
  deliveryId,
  headerId,
  headerText,
  categoryId,
}) => `${controlGroupId}-${deliveryId}@${headerId}@${headerText}$${categoryId}`;

export const parseChoiceId = (encodedId) => {
  if (!encodedId) {
    return false;
  }
  const [choiceId, headerId, headerText] = encodedId.split(
    constants.HEADER_SEPARATOR,
  );
  if (choiceId) {
    return {
      // eslint-disable-next-line no-mixed-operators
      choiceId:
        (headerId && choiceId.substring(3))
        || choiceId.split('$')[0].substring(3),
      headerId: headerId || '',
      // eslint-disable-next-line no-mixed-operators
      headerText:
        (headerText && getHeader(headerText.split('$')[0]))
        || constants.DEFAULT_HEADER_TEXT,
    };
  }
  return false;
};

export const isMobile = () => {
  const deviceType = Cookie.get('DT', 'MISCGCs');
  if (typeof deviceType === 'undefined') {
    return /Mobi/i.test(navigator.userAgent);
  }
  return deviceType !== 'PC';
};

export const isKillSwitchTrue = (
  property,
  ksList = {},
  isTrueIfNoMatch = false,
) => {
  if (!property || Object.keys(ksList).length === 0) {
    return isTrueIfNoMatch;
  }

  return ksList[property] === undefined ? isTrueIfNoMatch : !!ksList[property];
};

export const getCustomerShipLocation = () => Cookie.get(constants.CUSTOMER_SHIP_LOCATION);

export const getCountryCode = () => Cookie.get(constants.SHIPPING_COUNTRY_COOKIE_NAME);

export const getZipCode = () => Cookie.get(constants.ZIP_CODE_COOKIE_NAME, constants.MISCGCS);

export const getStateCode = () => Cookie.get(constants.STATE_COOKIE_NAME, constants.MISCGCS);

export const isMcom = () => process.env.ENTRY_POINT === constants.ENTRY_POINT.MCOM;

export const getCustomerId = () => {
  const uid = isMcom() ? constants.MCOM_ONLINE_UID : constants.BCOM_ONLINE_UID;
  return Cookie.get(uid);
};

export const cookie = typeof document !== 'undefined' ? document.cookie : null;
export const getCustomerType = () => Cookie.getCustomerStateFromCookie(cookie);
export const getExperimentIdsFromSeedCookie = () => {
  const experimentIdsFromSeedCookie = (Cookie.get('SEED') || '').match(/\d\d\d\d-\d\d/g);

  if (!experimentIdsFromSeedCookie) {
    return;
  }

  // eslint-disable-next-line consistent-return
  return experimentIdsFromSeedCookie.join(',');
};

export const guid = () => {
  const S4 = () => {
    const d = new Date();
    // eslint-disable-next-line no-bitwise
    return ((d.getTime() + Math.random()) * 0x10000 || 0)
      .toString(16)
      .substring(1);
  };

  return S4() + S4() + S4() + S4();
};

const validVisitorId = (visitorId) => visitorId && visitorId.length < 100;

export const getVisitorId = () => {
  const rtdCookie = Cookie.get(constants.RTD_COOKIE_NAME)?.trim() || '';

  if (!validVisitorId(rtdCookie)) {
    const visitorId = guid();
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 30);
    Cookie.set(constants.RTD_COOKIE_NAME, visitorId, null, {
      expires: expirationDate,
    });
    return visitorId;
  }

  return rtdCookie;
};

export const getAdobeVisitorId = () => {
  const adobeCookie = Cookie.get(constants.ADOBE_COOKIE_NAME)?.trim() || '';
  const unparsedCookieArray = adobeCookie.split('|');
  const mcmidIndex = unparsedCookieArray.indexOf(constants.MCMID);
  return mcmidIndex !== -1 ? unparsedCookieArray[mcmidIndex + 1] : '';
};

export const getSearchedTerm = () => {
  const url = new URL(window.location.href);

  if (url.pathname.includes('featured')) {
    const params = url.pathname.split('/');
    const index = params.indexOf('featured') + 1;
    const search = params[index].replace(/\?|#.*/, '');

    return decodeURI(search);
  }

  if (url.searchParams.has('keyword')) {
    return url.searchParams.get('keyword');
  }

  return '';
};

export const fetchFacetFilters = (link, isAllowedParam, createFacetKey) => {
  const facetLinkPart = link.split('/');
  if (facetLinkPart.length < 3) return '';

  const facetLink = link.split('/').slice(facetLinkPart.length - 2);
  const facetKeys = facetLink[0].split(',');
  const facetValues = facetLink[1].split(',');
  const facetParams = {};

  if (facetLinkPart.length < 3 || facetKeys.length !== facetValues.length) {
    return '';
  }
  const createFacetValue = (facetValue) => facetValue?.split('|').map((el) => decodeURIComponent(el));

  facetKeys.forEach((element) => {
    if (isAllowedParam(element)) {
      const facetKey = createFacetKey(element);
      let facetValue = createFacetValue(
        facetValues[facetKeys.indexOf(element)],
      );
      if (facetKey === 'price_discount_range') {
        facetValue = facetValue.map((val) => val.replace('_PERCENT_', '%'));
      }
      if (facetKey === 'price') {
        facetValue = facetValue.map((val) => val.replace('|', '-'));
      }
      facetParams[facetKey] = facetValue;
    }
  });
  return Object.keys(facetParams)
    .map(
      (key) => `${key}=${Array.isArray(facetParams[key])
        ? facetParams[key].join(',')
        : facetParams[key]
        }`,
    )
    .join('|');
};

export const facetFiltersForHPZone = (pathname) => {
  const url = new URL(`https://www.macys.com${pathname}`);
  return fetchFacetFilters(
    decodeURI(url.pathname).replace(/\+/g, ' '),
    () => true,
    (facetKey) => facetKey.toLowerCase(),
  );
};

export const facetFilters = () => {
  const allowedFacetKeys = {
    category: ['Department_type', 'Product_department', 'Mens_product_type'],
    brand: 'Brand',
    offers: ['Offers', 'Special_offers'],
    size: 'Size',
    color: 'Color_normal',
    price: 'Price',
  };

  const isAllowedParam = (param) => param.toLowerCase().includes('size')
    || Object.values(allowedFacetKeys).flat().includes(param);
  const createFacetKey = (facetKey) => (facetKey.toLowerCase().includes('size')
    ? facetKey
    : Object.keys(allowedFacetKeys).find(
      (key) => allowedFacetKeys[key] === facetKey
        || allowedFacetKeys[key].indexOf(facetKey) !== -1,
    ));

  return fetchFacetFilters(
    decodeURI(window.location.pathname).replace(/\+/g, ' '),
    isAllowedParam,
    createFacetKey,
  );
};

export const getPersistent = (key) => localStorage.getItem(key) || null;

export const getRecentlyViewedItems = () => {
  const prodIds = getPersistent(constants.RECENTLY_VIEWED_ITEMS);
  return typeof prodIds === 'string' ? prodIds.split(',') : [];
};

export const addRecentlyViewedItems = (id) => {
  let productId = parseInt(id, 10);

  if (Number.isNaN(productId) || productId.toString() !== id.toString()) {
    return;
  }

  productId = productId.toString();

  const productIdList = [
    productId,
    ...getRecentlyViewedItems().filter((existingId) => existingId !== productId),
  ]
    .slice(0, constants.RECENTLY_VIEWED_ITEMS_MAX_LIMIT[process.env.ENTRY_POINT]);

  localStorage.setItem(constants.RECENTLY_VIEWED_ITEMS, productIdList.join(','));
};

/**
 * Checks if the product object has all the necessary properties.
 * @param {Object} [product={}] - The product object to validate.
 * @property {Object} product.identifier - The product's identifier.
 * @property {Object} product.detail - The product's detail.
 * @property {boolean} product.availability.available - Whether the product is available.
 * @property {Object} product.pricing - The product's pricing.
 * @returns {boolean} Returns true if the product object has all the necessary properties, false otherwise.
 */
export const isValidProduct = (product = {}) => {
  const productObj = product?.product?.[0] || product;
  if (!productObj) {
    return false;
  }
  const {
    identifier, detail, availability, pricing,
  } = productObj;
  return Boolean(identifier && detail && availability?.available && pricing);
};

/**
 * Generates the grid cell style based on the number of items per breakpoint.
 * @param {Object} numberOfItems - Object containing the number of items per breakpoint.
 * @returns {string} - The generated grid cell style.
 */
export const getGridCellStyle = (numberOfItems) => {
  const { SMALL, MEDIUM, LARGE } = constants.BREAK_POINT_TYPE;
  const sizes = [SMALL, MEDIUM, LARGE];
  const totalColumns = 12;
  let style = 'cell grid-thumbnail';

  sizes.forEach((size) => {
    const sizeData = numberOfItems?.[size];
    if (sizeData?.column) {
      const isFiveColumn = sizeData.column === 5;
      const rounded = Math.floor(totalColumns / sizeData.column);
      const newClass = isFiveColumn ? ' grid-column-5' : ` ${size}-${rounded}`;
      style += newClass;
    }
  });

  return style;
};

export const getDeviceType = () => {
  const { SMALL, MEDIUM, LARGE } = constants.BREAK_POINT_TYPE;
  const { SMALL: SMALL_BP, MEDIUM: MEDIUM_BP } = constants.BREAK_POINTS;
  const { innerWidth } = window;

  if (innerWidth < SMALL_BP) {
    return SMALL;
  }
  if (innerWidth < MEDIUM_BP) {
    return MEDIUM;
  }
  return LARGE;
};

/**
 *
 * @param {Object} obj1 The destination object.
 * @param {Object} obj2 The source object.
 * @returns {Object}
 * @example
 * const obj1 = {
 *   'a': { 'b': 2 },  'd': 4
 * }
 *
 * const obj2 = {
 *   'a': { 'b':5, 'c': 3 }, 'e': 5
 * }
 *
 * merge(obj1, obj2) => { 'a': { 'b': 5, 'c': 3 },  'd': 4, 'e': 5  }
 */
export const merge = (obj1, obj2) => {
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj2;
  }

  const merged = { ...obj1 };
  Object.entries(obj2).forEach(([key, value]) => {
    if (
      key in merged
      && typeof merged[key] === 'object'
      && typeof value === 'object'
    ) {
      merged[key] = merge(merged[key], value);
    } else {
      merged[key] = value;
    }
  });

  return merged;
};

// remove undefined values
export const removeUndefinedValuesFromObject = (object) => {
  Object.keys(object).forEach(
    (k) => object[k] === undefined && delete object[k],
  );
  return object;
};

export const getCategoryPageUrl = ({
  recommendationConfig: { categoryId, zone },
} = {}) => {
  if (giftZones[zone] !== undefined) {
    return giftZones[zone];
  }
  return categoryId ? `/shop/gift-guide/gifts-under-25?id=${categoryId}` : null;
};

export const getProductListPageUrl = ({
  recommendationData,
  recommendationConfig,
}) => {
  if (
    !Object.keys(recommendationData ?? {}).length > 0
    || !Object.keys(recommendationConfig ?? {}).length > 0
  ) {
    return null;
  }

  const { zone } = recommendationConfig;
  const {
    recommendedIdsList,
    choiceId: rawChoiceId,
    content: { contentId: rawContentId } = {},
  } = recommendationData;

  const choiceId = (rawChoiceId.split('-')[0] || '').replace(/^cid/, '');
  const contentId = (rawContentId || '').replace(/^content_/i, '');
  let targetPath = '/shop/featured/';
  recommendedIdsList.forEach((product, index) => {
    targetPath += (index === 0 ? '' : '|') + product.id;
  });
  targetPath += `?prosSource=con&prosContent=${contentId}&prosZone=${zone}&prosChoiceId=${choiceId}`;
  return targetPath;
};

export const getContextUrlParams = (config) => {
  const params = {
    clientId: constants.PROS_UPPERCASE,
    currencyCode: `${Cookie.get(constants.CURRENCY_CODE) || 'USD'}`,
    _regionCode: `${Cookie.get(constants.SHIPPING_COUNTRY_COOKIE_NAME) || 'US'
      }`,
    size: 'small',
  };
  if (config?.linkUrl && config.linkUrl.toUpperCase().includes(constants.UPC_BOPS_PURCHASABLE)) {
    params._storeLocation = getCustomerShipLocation();
    params.includeBopsOnlyUpcs = true;
  }

  if (config?.enableColorRecommendations) {
    params.swatchColor = config?.recommendationConfig?.selectedColorFamily || '';
  }

  const experimentIdsFromSeedCookie = getExperimentIdsFromSeedCookie();

  if (experimentIdsFromSeedCookie) {
    params._customerExperiment = experimentIdsFromSeedCookie;
  }

  // TODO finish part below when meta data available
  // let context = {};

  // if (PageApp.getMeta) {
  //   context = PageApp.getMeta('context');
  //   if (context) {
  //     if (context.shoppingMode) {
  //       params.push(`_shoppingMode=${context.shoppingMode}`);
  //     }
  //     if (context.customerExperiment) {
  //       params.push(`_customerExperiment=${context.customerExperiment}`);
  //     }
  //     if (context.pdpServer) {
  //       params.push(`pdpserver=${context.pdpServer}`);
  //     }
  //   } else {
  //     params.push(`_customerExperiment=${ExperimentationSDK.getExpIds() || 'NO_EXPERIMENT'}`);
  //   }
  // }

  // params.push(`_customerState=${this.getCustomerState()}`);

  return params;
};

export const getRequester = (entryPoint = 'MCOM') => {
  const isBCOM = entryPoint.toUpperCase() === 'BCOM';
  if (isBCOM) {
    return window?.innerWidth < constants.BREAK_POINTS.SMALL
      ? 'BCOM-BMEW'
      : 'BCOM-NAVAPP';
  }
  return isMobile() ? 'MCOM-MMEW' : 'MCOM-NAVAPP';
};

export const getProductIdsWithPos = (items) => {
  if (typeof items[0] === 'object') {
    return items
      .map(({ productId, pos }) => `${productId}_Pos${pos}`)
      .join('|');
  }

  return items.map((i, idx) => `${i}_Pos${idx + 1}`).join('|');
};

/**
 * @param querystring
 * @return {string}
 */
export const getQueryString = (querystring = window.location.search) => querystring;

/**
 * @param {string} name
 * @param {string=} querystring
 * @returns {string}
 */
export const getURLParameter = (name, querystring) => (name ? new URLSearchParams(getQueryString(querystring)).get(name) : '');

/**
 * Filters out products with 'PWP' or 'GWP' type names from the given array of products.
 *
 * @param {Array} products - The array of products to filter.
 * @returns {Array} - The filtered array of products.
 */
export const filterGWPAndPWPProducts = (products) => products.filter((p) => !['PWP', 'GWP'].includes(p.detail.typeName));

const { SMALL, MEDIUM, LARGE } = constants.BREAK_POINT_TYPE;

export const getProductThumbnailSize = (numberOfItems) => {
  const { innerWidth } = window;
  let numberOfItem = numberOfItems[LARGE]?.column || numberOfItems[LARGE];
  if (innerWidth < constants.BREAK_POINTS.SMALL) {
    numberOfItem = numberOfItems[SMALL]?.column || numberOfItems[SMALL];
  } else if (innerWidth < constants.BREAK_POINTS.MEDIUM) {
    numberOfItem = numberOfItems[MEDIUM]?.column || numberOfItems[MEDIUM];
  }
  return numberOfItem === 1 ? constants.RESPONSIVE_PROS_WIDTH.LARGE : Math.ceil(innerWidth / numberOfItem);
};

/**
 * when element will be visible
 *
 * @param {HTMLElement | NodeList | array} element
 * @param {function} cb
 * @param {number} [threshold=400]
 * @returns {*}
 */
export const onElementWillBeVisible = (element, cb, rootMargin = 400, threshold = 10) => {
  const isValid = element && cb && typeof cb === 'function';

  if (!isValid) return false;

  // begin: workaround for empty div
  const setMinHeight = (item) => { item.style.minHeight = '1px'; };
  if (Array.isArray(element) || element instanceof NodeList) {
    [...element].forEach((item) => setMinHeight(item));
  } else {
    setMinHeight(element);
  }
  // end: workaround

  const options = {
    rootMargin: `${rootMargin}px`,
    threshold: threshold / 100,
  };
  const io = new IntersectionObserver(() => {
    io.destroy();
    cb(this);
  }, options);

  return io.ready.then(() => io.subscribe(element));
};

export const getShop5ExploreAttributes = (product) => {
  const attr = [];
  let prosControlGroupId;
  let batchId;
  let headerId;
  const choiceId = product.choiceId || '';
  const prosPos = `Pos${product.position || ''}`;

  if (choiceId && choiceId.split('-') && choiceId.split('-').length) {
    prosControlGroupId = choiceId.split('-')[0].substring(3, 9);
  }

  if (choiceId) {
    // eslint-disable-next-line no-useless-escape
    batchId = choiceId.match((/\-([\w\d\-]+)/));
    headerId = choiceId.match(/@(H.+?)@/);
  }
  attr[0] = undefined;
  if (prosPos && prosControlGroupId) {
    batchId = (batchId && batchId[1]) ? batchId[1] : '';
    headerId = (headerId && headerId.length) ? headerId[1] : '';
    const sourceCatId = product.relationships?.taxonomy?.defaultCategoryIdproducts || '';

    const ids = [prosPos, prosControlGroupId];
    if (batchId) {
      ids.push(batchId);
    }
    if (headerId) {
      ids.push(headerId);
    }
    if (sourceCatId) {
      ids.push(sourceCatId);
    }

    attr[1] = ids.join(' | ');
  }
  attr[13] = product.detail?.flags?.bigTicketItem === true ? 'Big Ticket' : undefined;
  attr[17] = product.detail?.flags?.isStoreOnlyProductOnline ? 'store_only_product' : undefined;
  attr[18] = product.availability?.available ? undefined : 'Product Unavailable';
  attr[21] = product.imagery?.videoId !== undefined ? 'ENHANCED_FEATURES_VIDEO' : undefined;
  attr[30] = product.detail?.flags?.PDPColorized === true ? 'PDP Colorized' : 'PDP Non Colorized';
  attr[31] = product.detail?.additionalImagesCount;

  attr.shift();
  return attr;
};

export const setATBCookies = (response) => {
  let expiryDate = new Date(new Date().getTime() + constants.COOKIES.THIRTHY_DAYS_IN_MS);
  Cookie.set(constants.COOKIES.USER_ID, response.bag.meta.userId, null, { expires: expiryDate });
  Cookie.set(constants.COOKIES.MACHINE_ID, response.bag.meta.machineId, null, { expires: expiryDate });
  Cookie.set(constants.COOKIES.BAG_GUID, response.bag.meta.bagGuid, null, { expires: expiryDate });
  expiryDate = new Date(new Date().getTime() + constants.COOKIES.ONE_DAY_IN_MS);
  if (response.bag.meta.dca) {
    Cookie.set(constants.COOKIES.DCA, response.bag.meta.dca, null, { expires: expiryDate });
  }
  if (response.bag.meta.xMacysWrapperId) {
    Cookie.set(constants.COOKIES.BAGWRAPPERID, response.bag.meta.xMacysWrapperId, null, { expires: expiryDate });
  }

  return true;
};

export const getImageId = (product, zone) => {
  if (zone && zone.includes('ATBPage')) {
    const imagePath = product.imagery?.primaryImage?.filePath;
    const regex = /optimized\/(\d+)_fpx/;
    const match = imagePath.match(regex);
    if (match && match[1]) {
      return match[1];
    }

    return '';
  }

  const selColor = product?.traits?.colors?.selectedColor;
  const { colorMap } = unref(product)?.traits?.colors || {};

  const images = (colorMap
    && Object.values(colorMap).find((c) => c.id === selColor)?.imagery?.images)
    || product.imagery?.images;

  return images ? images[0].name.replace('.fpx', '') : '';
};

export const getProductColorMap = (product, zone) => {
  if (zone && zone.includes('ATBPage')) {
    const color = product.traits?.colors[0];

    return `${product.id}-${color}`;
  }

  const colorMap = Object.values(product?.traits?.colors?.colorMap || {}).map((obj) => obj.name);

  return colorMap ? colorMap.map((color) => `${product.id}-${color}`).join('|') : '';
};

export const isPdpZone = (zone) => {
  const pdpZones = [
    prosZones.PDP_PAGE.PDP_ZONE_A,
    prosZones.PDP_PAGE.PDP_ZONE_B,
    prosZones.PDP_PAGE.PDP_ZONE_C,
    prosZones.PDP_PAGE.PDP_ZONE_F,
    prosZones.PDP_PAGE.PDP_ZONE_G,
    prosZones.PDP_PAGE.RVI_PDP,
    prosZones.PDP_PAGE.rvi_pdp_zone,
  ];

  return pdpZones.includes(zone);
};

export const generateUniqueString = (length = 30) => {
  const uniquePart = Date.now().toString(36);
  const randomPart = Array.from({ length: length - uniquePart.length }, () => Math.random().toString(36).charAt(2)).join('');

  return uniquePart + randomPart;
};
